import React, { useRef, useState, useEffect } from 'react';
import { Upload, Crosshair, AlertCircle, FileText } from 'lucide-react';

interface Coordinates {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Zone {
  id: string;
  name: string;
  type: 'text' | 'table' | 'checkbox' | 'date';
  coordinates: Coordinates;
  rules: any[];
  required: boolean;
}

interface ZoneSelectorProps {
  zones: Zone[];
  selectedZone: Zone | null;
  onAddZone: (zone: Omit<Zone, 'id'>) => void;
  onSelectZone: (zone: Zone | null) => void;
  onUpdateZone: (zone: Zone) => void;
}

const ZoneSelector: React.FC<ZoneSelectorProps> = ({
  zones,
  selectedZone,
  onAddZone,
  onSelectZone,
  onUpdateZone
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<{ x: number; y: number } | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [scale, setScale] = useState(1);
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isPdf, setIsPdf] = useState(false);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    console.log("File selected:", file.name, file.type, file.size);
    setFileName(file.name);
    setError(null);
    setLoading(true);
    
    // Check if it's a PDF
    const isPdfFile = file.type === 'application/pdf';
    setIsPdf(isPdfFile);
    
    try {
      if (isPdfFile) {
        // For PDFs, create an object URL to use with the PDF viewer
        const objectUrl = URL.createObjectURL(file);
        setPdfUrl(objectUrl);
        
        // Create a static preview image for PDFs
        setPreviewImage('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAzMDAgNDAwIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iIzIxMjEyMSIgLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iI2E4NTVmNyIgc3R5bGU9ImZvbnQtZmFtaWx5OiBBcmlhbDsgZm9udC1zaXplOiAyNHB4OyI+UERGIERvY3VtZW50PC90ZXh0Pjwvc3ZnPg==');
      } else {
        // Regular image handling
        const reader = new FileReader();
        
        reader.onload = (e) => {
          const result = e.target?.result as string;
          console.log("Image loaded, data URL length:", result?.length);
          setPreviewImage(result);
          setPdfUrl(null);
        };
        
        reader.onerror = () => {
          throw new Error(reader.error?.message || "Unknown error reading file");
        };
        
        reader.readAsDataURL(file);
      }
    } catch (err) {
      console.error("Error processing file:", err);
      setError(`Error processing file: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const getRelativeCoordinates = (event: React.MouseEvent): { x: number; y: number } => {
    const container = containerRef.current;
    if (!container) return { x: 0, y: 0 };

    const rect = container.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) / scale,
      y: (event.clientY - rect.top) / scale
    };
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    if (!isSelecting || !previewImage) return;
    setSelectionStart(getRelativeCoordinates(event));
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (!isSelecting || !selectionStart || !previewImage) return;

    const current = getRelativeCoordinates(event);
    const selection = document.getElementById('selection-box');
    if (selection) {
      const left = Math.min(selectionStart.x, current.x);
      const top = Math.min(selectionStart.y, current.y);
      const width = Math.abs(current.x - selectionStart.x);
      const height = Math.abs(current.y - selectionStart.y);

      selection.style.left = `${left}px`;
      selection.style.top = `${top}px`;
      selection.style.width = `${width}px`;
      selection.style.height = `${height}px`;
    }
  };

  const handleMouseUp = (event: React.MouseEvent) => {
    if (!isSelecting || !selectionStart || !previewImage) return;

    const end = getRelativeCoordinates(event);
    const coordinates = {
      x: Math.min(selectionStart.x, end.x),
      y: Math.min(selectionStart.y, end.y),
      width: Math.abs(end.x - selectionStart.x),
      height: Math.abs(end.y - selectionStart.y)
    };

    if (coordinates.width > 10 && coordinates.height > 10) {
      onAddZone({
        name: `Zone ${zones.length + 1}`,
        type: 'text',
        coordinates,
        rules: [],
        required: false
      });
    }

    setSelectionStart(null);
    setIsSelecting(false);

    const selection = document.getElementById('selection-box');
    if (selection) {
      selection.style.width = '0';
      selection.style.height = '0';
    }
  };

  const handleZoneClick = (zone: Zone, event: React.MouseEvent) => {
    event.stopPropagation();
    onSelectZone(zone.id === selectedZone?.id ? null : zone);
  };

  return (
    <div className="h-full flex flex-col">
      {/* File upload and controls */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <label className={`flex items-center gap-2 px-4 py-2 ${fileName ? 'bg-green-500/20 text-green-300' : 'bg-purple-500/20 text-purple-300'} hover:bg-purple-500/30 rounded-lg cursor-pointer transition-colors`}>
            <Upload className="w-4 h-4" />
            {fileName ? fileName : "Upload Template"}
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={handleImageUpload}
              className="hidden"
            />
          </label>
          <button
            onClick={() => setIsSelecting(!isSelecting)}
            disabled={!previewImage}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              isSelecting
                ? 'bg-purple-500 text-white'
                : previewImage 
                  ? 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30' 
                  : 'bg-purple-500/10 text-purple-400/50 cursor-not-allowed'
            }`}
          >
            <Crosshair className="w-4 h-4" />
            {isSelecting ? 'Cancel Selection' : 'Add Zone'}
          </button>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-200">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Info for PDF files */}
      {isPdf && fileName && !error && (
        <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center gap-2 text-blue-200">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <p className="text-sm">PDF detected. Using a placeholder image for zone selection. Zones will apply to the actual PDF document.</p>
        </div>
      )}

      {/* Main content area */}
      <div
        ref={containerRef}
        className="flex-1 relative border border-purple-500/20 rounded-lg overflow-hidden bg-black/20"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin mb-3"></div>
              <p className="text-purple-300">Loading template...</p>
            </div>
          </div>
        ) : previewImage ? (
          <div className="relative w-full h-full flex justify-center">
            <img
              src={previewImage}
              alt="Template"
              className="max-w-full h-auto object-contain"
              style={{ border: '1px solid rgba(139, 92, 246, 0.05)' }}
            />
            
            <div
              id="selection-box"
              className="absolute border-2 border-purple-500 bg-purple-500/10 pointer-events-none"
              style={{ left: 0, top: 0, width: 0, height: 0 }}
            />
            
            {zones.map((zone) => (
              <div
                key={zone.id}
                onClick={(e) => handleZoneClick(zone, e)}
                className={`absolute border-2 ${
                  zone.id === selectedZone?.id
                    ? 'border-purple-500 bg-purple-500/20'
                    : 'border-purple-500/50 bg-purple-500/10 hover:bg-purple-500/20'
                } cursor-pointer transition-colors`}
                style={{
                  left: `${zone.coordinates.x}px`,
                  top: `${zone.coordinates.y}px`,
                  width: `${zone.coordinates.width}px`,
                  height: `${zone.coordinates.height}px`
                }}
              >
                <div className="absolute top-0 left-0 transform -translate-y-full bg-purple-500 text-white text-xs px-2 py-1 rounded-t-lg">
                  {zone.name}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-md">
              <div className="flex justify-center mb-4">
                <div className="bg-purple-500/10 p-4 rounded-full">
                  <FileText className="w-12 h-12 text-purple-400" />
                </div>
              </div>
              <h3 className="text-lg font-medium text-purple-100 mb-2">No Template Document</h3>
              <p className="text-purple-400/60 mb-6">
                Upload a template document (PDF or image) to start creating extraction zones
              </p>
              <label className="inline-flex items-center gap-2 px-6 py-3 bg-purple-500 hover:bg-purple-600 rounded-lg text-white cursor-pointer transition-colors">
                <Upload className="w-5 h-5" />
                Upload Template
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ZoneSelector;